import React, { useState, useMemo } from "react";
import {
  Dialog,
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  Chip,
  alpha,
  useTheme,
  Slide,
  Paper,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import TableCard from "../TableCard";
import { getTableStatus, getStatusColor } from "../../utils/tableUtils";
import POSKeyboard from "../POSKeyboard";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TableSwitcherDialog = ({
  open,
  onClose,
  tables,
  selectedTable,
  onSelectTable,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [activeKb, setActiveKb] = useState(null);

  // Get unique locations
  const locations = useMemo(() => {
    const locs = [...new Set(tables.map((t) => t.location).filter(Boolean))];
    return ["All", ...locs];
  }, [tables]);

  // Filter tables
  const filteredTables = useMemo(() => {
    return tables.filter((table) => {
      // Only show available tables (check if table has active orders)
      const hasActiveOrders = table.Orders && table.Orders.length > 0;
      if (!table.isActive || hasActiveOrders) {
        return false;
      }

      // Location filter
      if (selectedLocation !== "All" && table.location !== selectedLocation) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const search = searchQuery.toLowerCase();
        const tableName = (table.name || "").toLowerCase();
        const tableNumber = `${table.tableNumber || ""}`;
        return tableName.includes(search) || tableNumber.includes(search);
      }

      return true;
    });
  }, [tables, selectedLocation, searchQuery]);

  const handleTableClick = (table) => {
    onSelectTable(table);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: "#f5f5f5",
        },
      }}
    >
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Switch Table
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a table to continue placing order
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              width: 48,
              height: 48,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            type="text"
            inputMode="text"
            autoComplete="off"
            placeholder="Search by table number or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setActiveKb("search")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                height: 48,
              },
            }}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, fontSize: 22, color: "text.secondary" }} />
              ),
            }}
          />
        </Box>

        {/* Location Filter */}
        {locations.length > 1 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {locations.map((location) => (
              <Chip
                key={location}
                label={location}
                onClick={() => setSelectedLocation(location)}
                sx={{
                  height: 44,
                  fontSize: "0.95rem",
                  px: 1,
                  fontWeight: 600,
                  bgcolor:
                    selectedLocation === location
                      ? theme.palette.primary.main
                      : theme.palette.action.hover,
                  color:
                    selectedLocation === location
                      ? "white"
                      : theme.palette.text.primary,
                  "&:hover": {
                    bgcolor:
                      selectedLocation === location
                        ? theme.palette.primary.dark
                        : theme.palette.action.selected,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
        }}
      >
        {/* Tables Grid */}
        <Grid container spacing={2}>
          {filteredTables.map((table) => {
            const status = getTableStatus(table);
            const statusInfo = getStatusColor(status, theme);
            const activeOrder = null; // Can be populated if order data is available
            const isSelected = selectedTable?.id === table.id;

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={table.id}>
                <Box
                  sx={{
                    outline: isSelected
                      ? `3px solid ${theme.palette.primary.main}`
                      : "none",
                    outlineOffset: 2,
                    borderRadius: 2,
                  }}
                >
                  <TableCard
                    table={table}
                    status={status}
                    statusInfo={statusInfo}
                    activeOrder={activeOrder}
                    onClick={() => handleTableClick(table)}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Empty State */}
        {filteredTables.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 10,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              No tables found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search or filter to find tables
            </Typography>
          </Box>
        )}
      </Box>

      {/* POSKeyboard */}
      <Slide
        direction="up"
        in={activeKb === "search"}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            bgcolor: alpha("#000", 0.3),
            pb: 2,
            pt: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <POSKeyboard
              value={searchQuery}
              onChange={setSearchQuery}
              onClose={() => setActiveKb(null)}
              onEnter={() => setActiveKb(null)}
              numericOnly={false}
            />
          </Box>
        </Box>
      </Slide>
    </Dialog>
  );
};

export default TableSwitcherDialog;
